import { useEffect, useState } from 'react';
import { getProducts } from '../service';
import { getProductConditions } from '../service/getProductConditions';

import { useAuth } from 'react-oidc-context';
import {
  DoctorActionHookReturn,
  DoctorProductResponse,
  DoctorProductTableProps,
  ProductType,
} from '../interface/interface';
import { useSocket } from './useSocket';
import { MultiValue, SingleValue } from 'react-select';
import { PaginationState } from '@tanstack/react-table';

const useProductActions = (producType: ProductType): DoctorActionHookReturn => {
  const { user } = useAuth();
  const [, , , , refreshProducts] = useSocket(user?.access_token as string);
  const [loading, setLoading] = useState<boolean>(true);

  // Data Store
  const [productList, setProductList] = useState<
    Array<DoctorProductTableProps> | []
  >([]);
  const [productCategory, setProductCategory] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [productCondition, setProductCondition] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [productFormulation, setProductFormulation] = useState<
    Array<{ label: string; value: string }>
  >([]);
  // Search
  const [search, setSearch] = useState('');
  const [submitSearch, setSubmitSearch] = useState<number>(0);

  // Filters
  const [filter, setFilter] = useState<
    SingleValue<{
      label: string;
      value: string;
    }>
  >({ label: 'Active', value: 'active' });

  const [sortByCategory, setSortByCategory] = useState<
    MultiValue<{
      label: string;
      value: string;
    }>
  >([]);

  const [sortByCondition, setSortByCondition] = useState<
    MultiValue<{
      label: string;
      value: string;
    }>
  >([]);

  const [sortByFormulation, setSortByFormulation] = useState<
    MultiValue<{
      label: string;
      value: string;
    }>
  >([]);

  // Pagination
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 300,
  });
  const [paginationCount, setPaginationCount] = useState<number>(1);
  const [submitCount, setSubmitCount] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  // Fetching
  useEffect(() => {
    if (!user?.access_token) {
      return;
    }

    getProducts(
      user.access_token,
      producType,
      search,
      {
        sortByCategory: localStorage.getItem('productCategroy')
          ? JSON.parse(localStorage.getItem('productCategroy') as string).map(
              (x: { value: string }) => x.value,
            )
          : sortByCategory.map((x) => x.value),
        sortByCondition: localStorage.getItem('productCondition')
          ? JSON.parse(localStorage.getItem('productCondition') as string).map(
              (x: { value: string }) => x.value,
            )
          : sortByCondition.map((x) => x.value),
        sortByFormulation: localStorage.getItem('productFormulation')
          ? JSON.parse(
              localStorage.getItem('productFormulation') as string,
            ).map((x: { value: string }) => x.value)
          : sortByFormulation.map((x) => x.value),
      },

      pagination,
      filter?.value,
    )
      .then(
        (data: {
          data: Array<DoctorProductResponse>;
          total: number;
          productMetas: {
            categories: string[];
            formulations: string[];
          };
        }) => {
          if (data?.productMetas?.categories?.length) {
            setProductCategory(
              data.productMetas.categories.map((x) => ({
                label: x,
                value: x,
              })),
            );
            setProductFormulation(
              data.productMetas.formulations.map((x) => ({
                label: x,
                value: x,
              })),
            );
          }

          if (!data.data?.length) {
            setProductList([]);
            setLoading(false);
            setTotalRecords(0);
            return;
          }

          if (
            paginationCount !== Math.ceil(data.total / pagination.pageSize) &&
            submitCount < submitSearch
          ) {
            setPaginationCount(Math.ceil(data.total / pagination.pageSize));
            setSubmitCount(submitSearch);
            setPagination({
              pageIndex: 0,
              pageSize: pagination.pageSize,
            });
          }

          setProductList(
            data?.data.map((ele) => ({
              ...ele,
              dayornight: ele?.dayOrNight?.name,
              category: ele?.productCategory?.name,
              formulation: ele?.productFormulation?.name,
              tga: ele?.tgaCategory?.short_name,
            })),
          );
          setTotalRecords(data?.total);
          setLoading(false);
        },
      )
      .catch((error) => {
        console.error('Error fetching products:', error);
        setLoading(false);
      });
  }, [
    pagination.pageIndex,
    submitSearch,
    refreshProducts,
    filter,
    sortByCategory.length,
    localStorage.getItem('productCategroy'),
    sortByCondition,
    localStorage.getItem('productCondition'),
    localStorage.getItem('productFormulation'),
  ]);

  useEffect(() => {
    if (!user?.access_token) {
      return;
    }

    getProductConditions(user?.access_token).then((data) =>
      setProductCondition(
        data?.map((x: { condition: string }) => ({
          label: x.condition,
          value: x.condition,
        })),
      ),
    );
  }, []);

  return [
    loading,
    {
      data: productList,
      productCategory,
      productCondition,
      productFormulation,
    },
    {
      value: search,
      changeHandler: setSearch,
      submitSearch: setSubmitSearch,
    },
    {
      filter,
      setFilter,
    },
    {
      sortByCategory,
      setSortByCategory,
      sortByCondition,
      setSortByCondition,
      sortByFormulation,
      setSortByFormulation,
    },
    {
      state: pagination,
      setter: setPagination,
      totalRecords,
    },
  ];
};

export default useProductActions;
